import { Mermaid } from "../components";

export function Technology() {
  return (
    <>
      <h1>Technology</h1>

      <p>
        This page is for investors doing technical due diligence. It covers
        infrastructure architecture, security stack, development practices,
        and organizational structure in detail.
      </p>
      <p>
        The infrastructure investment is deliberate. Enterprise customers in
        defense, healthcare, and finance run vendor security assessments
        before signing. Most startups fail these assessments and spend 3-6
        months rebuilding infrastructure to pass. TAIGA built
        enterprise-grade infrastructure from day one — not because it is
        fun, but because it removes the single biggest sales blocker in
        regulated industries. The security posture you see here is what
        closes deals in weeks instead of months.
      </p>

      <hr />

      <h2>Application Architecture</h2>
      <p>
        TAIGA runs two separate API surfaces to isolate AI workloads from
        data operations:
      </p>

      <table>
        <thead>
          <tr>
            <th>Layer</th>
            <th>Technology</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Data API</td>
            <td>Fastify 5 on ECS Fargate</td>
            <td>
              Request-response CRUD, auth, settings. Synchronous, fast,
              cacheable.
            </td>
          </tr>
          <tr>
            <td>AI API</td>
            <td>AWS Bedrock AgentCore Gateway</td>
            <td>
              Agent orchestration, streaming responses, tool calls,
              multi-turn conversations. Asynchronous, long-running.
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        This prevents AI workloads from blocking data operations and allows
        independent scaling. A misbehaving agent cannot take down the
        user-facing API.
      </p>

      <h3>Full Stack</h3>
      <table>
        <thead>
          <tr>
            <th>Layer</th>
            <th>Technology</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Frontend</td>
            <td>React 19 + Vite</td>
            <td>SPA, TypeScript-first</td>
          </tr>
          <tr>
            <td>UI</td>
            <td>Tailwind CSS + shadcn/ui</td>
            <td>Component library</td>
          </tr>
          <tr>
            <td>Backend</td>
            <td>Fastify 5 + TypeScript</td>
            <td>ECS Fargate (serverless containers)</td>
          </tr>
          <tr>
            <td>AI Runtime</td>
            <td>AWS Bedrock AgentCore</td>
            <td>Isolated microVMs per agent execution</td>
          </tr>
          <tr>
            <td>LLM Providers</td>
            <td>
              Anthropic Claude (primary) + OpenAI GPT-4o (council)
            </td>
            <td>Multi-model via Bedrock</td>
          </tr>
          <tr>
            <td>ORM</td>
            <td>Drizzle ORM</td>
            <td>Type-safe SQL, schema-first, migrations</td>
          </tr>
          <tr>
            <td>Database</td>
            <td>PostgreSQL 18</td>
            <td>Row-Level Security for tenant isolation</td>
          </tr>
          <tr>
            <td>Auth</td>
            <td>AWS Cognito</td>
            <td>
              OAuth 2.0 / OIDC, Google Workspace federation, Entra ID-ready
            </td>
          </tr>
          <tr>
            <td>Real-time</td>
            <td>WebSockets + SSE</td>
            <td>Chat streaming, live updates</td>
          </tr>
          <tr>
            <td>Object Storage</td>
            <td>S3 + CloudFront</td>
            <td>Static assets, documents, agent artifacts</td>
          </tr>
          <tr>
            <td>Monorepo</td>
            <td>pnpm workspaces + Turborepo</td>
            <td>Shared types, single dependency tree</td>
          </tr>
          <tr>
            <td>IaC</td>
            <td>Terraform</td>
            <td>State in S3, modular per-account</td>
          </tr>
        </tbody>
      </table>

      <h3>Key Application Patterns</h3>
      <table>
        <thead>
          <tr>
            <th>Pattern</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>LLM Council</td>
            <td>
              Multiple models evaluate independently, cross-review,
              synthesize. Higher accuracy for high-stakes generation.
            </td>
          </tr>
          <tr>
            <td>LFA</td>
            <td>
              Goal -&gt; Purpose -&gt; Outputs -&gt; Activities. Governance
              structure for every autonomous action.
            </td>
          </tr>
          <tr>
            <td>Three Loops</td>
            <td>
              Learning (spec) -&gt; Delivery (build) -&gt; Operations (run).
              Product lifecycle.
            </td>
          </tr>
          <tr>
            <td>Policy Cards</td>
            <td>
              Machine-readable governance rules embedded in the platform.
              Reusable across projects.
            </td>
          </tr>
          <tr>
            <td>Eval Harness</td>
            <td>
              5-tier coding agent evaluation: trivial -&gt; easy -&gt;
              medium -&gt; hard -&gt; expert. Data-driven improvement.
            </td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>Cloud Infrastructure</h2>
      <table>
        <thead>
          <tr>
            <th>Component</th>
            <th>Technology</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Cloud provider</td>
            <td>AWS</td>
            <td>EU regions only — home region eu-central-1 (Frankfurt)</td>
          </tr>
          <tr>
            <td>Account management</td>
            <td>AWS Control Tower + AFT</td>
            <td>Multi-account landing zone, 5 organizational units</td>
          </tr>
          <tr>
            <td>IaC</td>
            <td>Terraform</td>
            <td>Modular, per-account customizations</td>
          </tr>
          <tr>
            <td>CI/CD</td>
            <td>GitHub Actions</td>
            <td>16 reusable workflows</td>
          </tr>
          <tr>
            <td>Container runtime</td>
            <td>ECS Fargate</td>
            <td>Serverless containers — no EC2 to manage</td>
          </tr>
          <tr>
            <td>Container registry</td>
            <td>ECR</td>
            <td>Per-account, private</td>
          </tr>
          <tr>
            <td>DNS</td>
            <td>Route 53</td>
            <td>tai.ga domain</td>
          </tr>
          <tr>
            <td>CDN</td>
            <td>CloudFront</td>
            <td>Static assets + API acceleration</td>
          </tr>
        </tbody>
      </table>

      <h3>AWS Organization Structure</h3>
      <Mermaid
        label="AWS Organization Structure"
        chart={`
          graph TD
            Root[Management Account] --> Security[Security OU]
            Root --> Infra[Infrastructure OU]
            Root --> Sandbox[Sandbox OU]
            Root --> Workloads[Workloads OU]
            Root --> Suspended[Suspended OU]
            Security --> LogArchive[Log Archive<br/>centralized logging]
            Security --> Audit[Audit<br/>security tooling]
            Infra --> Network[Network Hub<br/>transit gateway, egress]
            Sandbox --> DevSandbox[Developer sandboxes<br/>per-person]
            Workloads --> Dev[Dev accounts]
            Workloads --> Staging[Staging accounts]
            Workloads --> Prod[Production accounts]
            Suspended --> Deactivated[Deactivated accounts]
        `}
      />

      <p>
        This is a multi-account landing zone with account-per-environment
        isolation. Workloads in dev cannot reach production. Developer
        sandboxes are fully isolated. This is the infrastructure pattern
        recommended by AWS for enterprise customers — unusual to see at
        pre-seed.
      </p>

      <hr />

      <h2>Security Stack</h2>
      <table>
        <thead>
          <tr>
            <th>Service</th>
            <th>Purpose</th>
            <th>Scope</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>GuardDuty</td>
            <td>Threat detection</td>
            <td>Org-wide, all regions</td>
          </tr>
          <tr>
            <td>Security Hub</td>
            <td>Compliance posture (FSBP + CIS v3.0.0)</td>
            <td>Org-wide</td>
          </tr>
          <tr>
            <td>Inspector</td>
            <td>Vulnerability scanning</td>
            <td>Org-wide</td>
          </tr>
          <tr>
            <td>Config</td>
            <td>Resource compliance</td>
            <td>Org-wide with conformance packs</td>
          </tr>
          <tr>
            <td>IAM Access Analyzer</td>
            <td>Unused access, external access</td>
            <td>Org-wide</td>
          </tr>
          <tr>
            <td>CloudTrail</td>
            <td>Audit logging</td>
            <td>Org trail, all regions, S3 + CloudWatch Logs</td>
          </tr>
          <tr>
            <td>SCPs</td>
            <td>Permission guardrails</td>
            <td>Deny non-EU regions, enforce encryption, restrict root</td>
          </tr>
          <tr>
            <td>Declarative Policies</td>
            <td>Resource configuration</td>
            <td>IMDSv2, VPC BPA, S3 BPA, EBS encryption</td>
          </tr>
        </tbody>
      </table>

      <p>
        Every security service runs org-wide across all accounts and all
        regions. SCPs enforce EU data residency at the infrastructure level
        — it is not possible to deploy resources outside EU regions. This is
        not a policy statement. It is a technical control.
      </p>

      <hr />

      <h2>Three-Layer Authorization</h2>
      <Mermaid
        label="Three-Layer Authorization"
        chart={`
          graph TD
            Req[Request] --> GW[API Gateway<br/>Token validation, rate limiting, WAF]
            GW --> RBAC[Role-Based Access<br/>Business logic authorization]
            RBAC --> RLS[PostgreSQL RLS<br/>Database-level tenant isolation]
        `}
      />

      <p>
        Each layer enforces independently. A compromised application layer
        cannot access another tenant's data because RLS enforces isolation
        at the database level. The database role used by the application
        cannot bypass RLS. A separate migration role handles schema
        changes.
      </p>

      <hr />

      <h2>Multi-Tenancy Design</h2>
      <ul>
        <li>
          Single shared database with Row-Level Security on all tenant-owned
          tables
        </li>
        <li>
          Every table carries a <code>tenant_id</code> column
        </li>
        <li>Tenant context set per transaction via session variables</li>
        <li>Application DB role cannot bypass RLS</li>
        <li>
          Separate migration role for schema changes (no tenant data access)
        </li>
        <li>
          Every mutation emits an immutable audit event: tenant_id, actor,
          action, target, changes, correlation_id, timestamp
        </li>
        <li>
          Audit events stored in append-only table — no UPDATE or DELETE
          permissions
        </li>
      </ul>

      <hr />

      <h2>Development Practices</h2>

      <h3>Engineering Principles</h3>
      <ul>
        <li>
          <strong>IaC everything</strong> — no ClickOps, ever
        </li>
        <li>
          <strong>Multi-account isolation</strong> — workloads do not share
          accounts
        </li>
        <li>
          <strong>Managed services first</strong> — do not self-host unless
          there is a reason
        </li>
        <li>
          <strong>Region-locked to EU</strong> — enforced by SCPs, not
          convention
        </li>
        <li>
          <strong>Trunk-based development</strong> — short-lived branches,
          frequent merges
        </li>
        <li>
          <strong>PR reviews required</strong> — humans review AI-generated
          code, not vice versa
        </li>
        <li>
          <strong>Tests prove correctness</strong> — not just coverage
          percentages
        </li>
        <li>
          <strong>Dependencies are attack surface</strong> — minimize and
          audit
        </li>
      </ul>

      <h3>AI Development Practices</h3>
      <ul>
        <li>
          <strong>AI is a tool, not a team member</strong> — it needs
          supervision and governance
        </li>
        <li>
          <strong>Every AI action is auditable</strong> — logs, traces,
          decision records
        </li>
        <li>
          <strong>Prompt engineering is engineering</strong> — version it,
          test it, review it
        </li>
        <li>
          <strong>Context is everything</strong> — AI without context
          produces confidently wrong answers
        </li>
      </ul>

      <h3>CI/CD Pipeline</h3>
      <p>16 GitHub Actions workflows covering:</p>

      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Tools</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Code quality</td>
            <td>ESLint, Prettier, TypeScript strict mode</td>
          </tr>
          <tr>
            <td>Testing</td>
            <td>Unit tests, integration tests</td>
          </tr>
          <tr>
            <td>Security scanning</td>
            <td>Trivy (containers), Checkov (IaC), Semgrep (code)</td>
          </tr>
          <tr>
            <td>Deployment</td>
            <td>
              Automated per-environment with GitHub Environment protection
            </td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>Repository Structure</h2>

      <h3>Platform (infrastructure and operations)</h3>
      <table>
        <thead>
          <tr>
            <th>Repo</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>taiga-aws-landingzone</td>
            <td>AWS org IaC (Control Tower, SCPs, security services)</td>
          </tr>
          <tr>
            <td>taiga-aft-*</td>
            <td>Account Factory customizations (4 repos)</td>
          </tr>
          <tr>
            <td>taiga-infra-network</td>
            <td>Network architecture (Transit Gateway, VPC peering)</td>
          </tr>
          <tr>
            <td>taiga-handbook</td>
            <td>
              Culture, values, engineering practices, ADRs, runbooks
            </td>
          </tr>
          <tr>
            <td>taiga-isms</td>
            <td>
              ISMS policies, risk register, vendor assessments, compliance
            </td>
          </tr>
          <tr>
            <td>taiga-ai-context</td>
            <td>AI agent knowledge base</td>
          </tr>
          <tr>
            <td>github-actions</td>
            <td>Reusable CI/CD workflows</td>
          </tr>
        </tbody>
      </table>

      <h3>Product (application and business)</h3>
      <table>
        <thead>
          <tr>
            <th>Repo</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>taiga-app</td>
            <td>Core platform (frontend + backend + agents)</td>
          </tr>
          <tr>
            <td>taiga-decks</td>
            <td>
              Pitch decks, investor materials, interactive deck system
            </td>
          </tr>
          <tr>
            <td>taiga-brand</td>
            <td>Brand book, design system, visual assets</td>
          </tr>
          <tr>
            <td>taiga-internal-it</td>
            <td>
              Internal IT automation (SCIM sync, Google Workspace)
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        This is not a monolith and it is not microservice sprawl. Each repo
        has a clear owner and a clear purpose. Infrastructure is separated
        from product. Operations tooling is separated from customer-facing
        code.
      </p>
    </>
  );
}
